import {
  type ClientTitle,
  ClientTitleLabels,
  type RiskAssessment,
} from '@nestjs-boilerplate/types';
import { format } from 'date-fns';

import { COUNTRIES } from '@/constants/countries';

type AssessmentWithDetails = RiskAssessment & {
  clientDetails?: Record<string, unknown>;
};

interface ReportClientDetailsProps {
  clientDetails: Record<string, unknown> | undefined;
  assessment: AssessmentWithDetails;
}

function getCountryName(code: string | undefined | null): string {
  if (!code) return 'N/A';
  const country = COUNTRIES.find((c) => c.value === code.toUpperCase());
  return country?.label || code;
}

function formatTitles(titles: string[] | null | undefined): string {
  if (!titles || titles.length === 0) return '';
  return titles.map((t) => ClientTitleLabels[t as ClientTitle] || t).join('/');
}

function formatFullName(clientDetails: Record<string, unknown> | undefined): string {
  if (!clientDetails) return 'N/A';
  return `${clientDetails.forename || ''} ${clientDetails.middleName || ''} ${clientDetails.surname || ''}`.trim();
}

function formatAddress(clientDetails: Record<string, unknown> | undefined): string {
  if (!clientDetails) return 'N/A';
  const parts = [
    clientDetails.addressLine1,
    clientDetails.addressLine2,
    clientDetails.townCity,
    clientDetails.county,
    clientDetails.postcode,
    clientDetails.countryOfResidence
      ? getCountryName(clientDetails.countryOfResidence as string)
      : null,
  ].filter(Boolean);
  return parts.join(', ') || 'N/A';
}

export function ReportClientDetails({ clientDetails, assessment }: ReportClientDetailsProps) {
  return (
    <section className="mb-8 rounded-lg border bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-[#051923]">Client Details</h2>
      <div className="flex flex-col gap-4">
        <div>
          <p className="text-sm text-gray-500">Client Reference</p>
          <p className="font-medium">
            {(clientDetails?.clientRef as string) || assessment.clientRef}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Title</p>
          <p className="font-medium">{formatTitles(clientDetails?.titles as string[]) || 'N/A'}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Full Name</p>
          <p className="font-medium">{formatFullName(clientDetails)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Date of Birth</p>
          <p className="font-medium">
            {clientDetails?.dateOfBirth
              ? format(new Date(clientDetails.dateOfBirth as string), 'dd/MM/yyyy')
              : 'N/A'}
          </p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Nationality</p>
          <p className="font-medium">{getCountryName(clientDetails?.nationality as string)}</p>
        </div>
        <div>
          <p className="text-sm text-gray-500">Address</p>
          <p className="font-medium">{formatAddress(clientDetails)}</p>
        </div>
      </div>
    </section>
  );
}
